import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CPM_EUR_HT, MIN_PRICE_EUR_HT } from '@/hooks/useVolumeEstimation';
import { OutputFormatSelector } from '@/components/OutputFormatSelector';
import { OutputFormat } from '@/constants/outputFormat';
import { VolumeTotals } from '@/components/VolumeGrid';

/**
 * État du formulaire pour UNE population (type de contact + volume demandé).
 * `active` indique si l'utilisateur veut traiter cette population.
 */
export interface PopulationFormEntry {
  active: boolean;
  contactType: string;
  requestedVolume: string;
}

export type PopulationFormState = Record<string, PopulationFormEntry>;

interface OrderFormProps {
  requestName: string;
  setRequestName: (value: string) => void;
  contactType: string;
  setContactType: (value: string) => void;
  outputFormat: OutputFormat;
  setOutputFormat: (value: OutputFormat) => void;
  requestedVolume: string;
  setRequestedVolume: (value: string) => void;
  validateVolume: { isValid: boolean; error: string };
  totalPrice: number;
  areButtonsEnabled: boolean;
  onGenerateQuote: () => void;
  onDeliveryClick: () => void;
  cpmPrice?: number;  // CPM du client (converti depuis string)
  minimumPrice?: number;  // Prix plancher du client
  isCreatingRequest?: boolean;  // État de chargement pendant la création de la demande
  // --- Mode population (rétro-compatible : absent => comportement actuel) ---
  hasPopulation?: boolean;
  populations?: string[];
  populationForm?: PopulationFormState;
  onPopulationEntryChange?: (population: string, patch: Partial<PopulationFormEntry>) => void;
  /** Volumes estimés par population (clé = nom de population). Pour le cap par population. */
  populationTotals?: Record<string, VolumeTotals>;
  /** Vrai une fois l'estimation lancée : active le cap par population. */
  estimateCalculated?: boolean;
}

const CONTACT_TYPE_OPTIONS = [
  { value: 'email', label: 'Emails' },
  { value: 'mobile', label: 'Mobiles' },
  { value: 'email_or_mobile', label: 'Emails ou Mobiles' },
  { value: 'email_and_mobile', label: 'Emails et Mobiles' },
];

/** Total estimé correspondant au type de contact sélectionné, ou null si indéterminable. */
const maxVolumeForContactType = (
  totals: VolumeTotals | undefined,
  contactType: string
): number | null => {
  if (!totals || !contactType) {
    return null;
  }
  switch (contactType) {
    case 'email':
      return totals.email;
    case 'mobile':
      return totals.mobile;
    case 'email_or_mobile':
      return totals.emailOrMobile;
    case 'email_and_mobile':
      return totals.emailAndMobile;
    default:
      return null;
  }
};

/**
 * Formate un nombre saisi en groupant les milliers par espace insécable visuel.
 * Retourne `null` si la saisie n'est pas numérique (les appelants testent `!== null`).
 */
const formatVolumeInput = (raw: string): string | null => {
  const value = raw.replace(/\s/g, '');
  if (value === '' || /^\d+$/.test(value)) {
    return value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }
  return null;
};

export const OrderForm = ({
  requestName,
  setRequestName,
  contactType,
  setContactType,
  outputFormat,
  setOutputFormat,
  requestedVolume,
  setRequestedVolume,
  validateVolume,
  totalPrice,
  areButtonsEnabled,
  onGenerateQuote,
  onDeliveryClick,
  cpmPrice,
  minimumPrice,
  isCreatingRequest = false,
  hasPopulation = false,
  populations = [],
  populationForm = {},
  onPopulationEntryChange,
  populationTotals = {},
  estimateCalculated = false,
}: OrderFormProps) => {
  // Utiliser les valeurs du client si disponibles, sinon les valeurs par défaut
  const cpm = cpmPrice ?? CPM_EUR_HT;
  const minPrice = minimumPrice ?? MIN_PRICE_EUR_HT;
  const cpmDisplay = `(${cpm.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}€ H.T. cpm | prix plancher ${minPrice.toLocaleString('fr-FR')}€ H.T.)`;

  const populationMode = hasPopulation && populations.length > 0;

  return (
    <div className="lg:w-[30%] w-full">
      <Card className="rounded-2xl shadow-md h-full flex flex-col min-h-0">
        <CardHeader className="flex-shrink-0 pb-3">
          <CardTitle className="text-lg">Paramétrage de livraison</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto min-h-0">
          <div className="space-y-3 pb-1">
            {/* Nom de demande */}
            <div>
              <Label htmlFor="request-name" className="text-sm font-medium">
                Nom de demande
              </Label>
              <Input
                id="request-name"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                placeholder="Ex: Campagne Automne 2024"
                className="mt-1"
              />
            </div>

            {populationMode ? (
              /* Mode population : une ligne dense par population
                 (checkbox + nom + type de contact + volume alignés). */
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Volume par population</Label>
                <div className="space-y-1">
                  {populations.map((population) => {
                    const entry: PopulationFormEntry = populationForm[population] ?? {
                      active: false,
                      contactType: '',
                      requestedVolume: '',
                    };
                    const checkboxId = `population-active-${population}`;
                    // Cap par population : seulement si estimation lancée + type choisi.
                    const cap =
                      estimateCalculated && entry.active
                        ? maxVolumeForContactType(populationTotals[population], entry.contactType)
                        : null;
                    const requestedNumber = parseInt(entry.requestedVolume.replace(/\s/g, ''), 10);
                    const isOverCap =
                      cap !== null && !isNaN(requestedNumber) && requestedNumber > cap;
                    return (
                      <div
                        key={population}
                        className={
                          'flex flex-wrap items-center gap-1.5 rounded-md border px-1.5 py-1 transition-colors ' +
                          (entry.active ? 'border-primary bg-primary/5' : 'border-border')
                        }
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={entry.active}
                          onCheckedChange={(checked) =>
                            onPopulationEntryChange?.(population, { active: checked === true })
                          }
                          className="shrink-0"
                        />
                        <Label
                          htmlFor={checkboxId}
                          className="min-w-0 flex-1 cursor-pointer truncate text-xs font-semibold"
                          title={population}
                        >
                          {population}
                        </Label>
                        <Select
                          value={entry.contactType}
                          onValueChange={(value) =>
                            onPopulationEntryChange?.(population, { contactType: value })
                          }
                          disabled={!entry.active}
                        >
                          <SelectTrigger
                            className="h-7 w-[7.5rem] shrink-0 px-2 text-xs"
                            aria-label={`Type de contact ${population}`}
                          >
                            <SelectValue placeholder="Type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_TYPE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          type="text"
                          value={entry.requestedVolume}
                          aria-label={`Volume demandé ${population}`}
                          aria-invalid={isOverCap}
                          disabled={!entry.active}
                          onChange={(e) => {
                            const formatted = formatVolumeInput(e.target.value);
                            if (formatted !== null) {
                              onPopulationEntryChange?.(population, { requestedVolume: formatted });
                            }
                          }}
                          placeholder="0"
                          className={
                            'h-7 w-[4.5rem] shrink-0 px-2 text-right text-xs ' +
                            (isOverCap ? 'border-destructive text-destructive focus-visible:ring-destructive' : '')
                          }
                        />
                        {isOverCap && cap !== null && (
                          <p className="w-full text-right text-[10px] text-destructive">
                            max {cap.toLocaleString('fr-FR')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Mode standard : Type de contact + Volume demandé (comportement actuel). */
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="contact-type" className="text-sm font-medium">
                    Type de contact
                  </Label>
                  <Select value={contactType} onValueChange={setContactType}>
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue placeholder="Choisir..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="volume" className="text-sm font-medium">
                    Volume demandé
                  </Label>
                  <Input
                    id="volume"
                    type="text"
                    value={requestedVolume}
                    onChange={(e) => {
                      const formatted = formatVolumeInput(e.target.value);
                      if (formatted !== null) {
                        setRequestedVolume(formatted);
                      }
                    }}
                    placeholder="0"
                    className="mt-1"
                  />
                  {validateVolume.error && (
                    <p className="text-xs text-destructive mt-1">{validateVolume.error}</p>
                  )}
                </div>
              </div>
            )}

            {/* Format du fichier de sortie (output_format) — global, 1 seul pour toute la demande */}
            <OutputFormatSelector value={outputFormat} onChange={setOutputFormat} />

            <div className="text-left">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-sm underline">Montant:</span>
                <span className="text-xs text-muted-foreground">{cpmDisplay}</span>
              </div>
              <div className="text-2xl font-bold text-success mt-1">
                {totalPrice.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} € <span className="text-lg">H.T.</span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={onGenerateQuote}
                className="flex-1 gap-2 bg-quote-violet hover:bg-quote-violet/90 text-white"
                disabled={!areButtonsEnabled || isCreatingRequest}
              >
                <FileText className="h-4 w-4" />
                {isCreatingRequest ? 'Création...' : 'Devis'}
              </Button>
              <Button
                onClick={onDeliveryClick}
                className="flex-1 bg-primary hover:bg-primary-hover text-primary-foreground"
                disabled={!areButtonsEnabled || isCreatingRequest}
              >
                <span>{isCreatingRequest ? 'Création...' : 'Livraison →'}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
